import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE } from '../config';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { SkeletonList } from '../components/ui/Skeleton';
import ErrorState from '../components/ui/ErrorState';
import Modal from '../components/ui/Modal';
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  MapPin,
  Briefcase,
  DollarSign,
  Building,
  ExternalLink,
  Play,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  X,
  Tag
} from 'lucide-react';

interface Job {
  id: number;
  title: string;
  company_name: string;
  location: string;
  job_type: string;
  experience_level: string;
  salary_range: string | null;
  posting_date: string;
  application_url: string | null;
  description: string | null;
  description_plain: string | null;
  remote_policy: string | null;
  scraped_at: string;
  status: string;
  last_seen_at: string | null;
  closed_at: string | null;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  limit: number;
  has_next_page: boolean;
  has_prev_page: boolean;
}

interface Company {
  id: number;
  name: string;
  industry: string;
  category: string;
  career_page_url: string;
  last_scraped: string | null;
}

interface JobSkill {
  skill: string;
  skill_category: string;
  confidence: number;
  source: string;
}

const SKILL_CATEGORY_COLORS: Record<string, string> = {
  technical: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400',
  tool: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  certification: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  soft: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  domain: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
};

function stripHtml(html: string): string {
  // Pass 1: decode HTML entities (turns &lt;h2&gt; into <h2>)
  const decoded = new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
  // Pass 2: strip actual HTML tags (turns <h2>text</h2> into text)
  return new DOMParser().parseFromString(decoded, 'text/html').body.textContent || '';
}

const Jobs = () => {
  const { toast } = useToast();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scraping, setScraping] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expFilter, setExpFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [skillFilter, setSkillFilter] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [salaryFilter, setSalaryFilter] = useState('');
  const [countries, setCountries] = useState<{ value: string; count: number }[]>([]);
  const [locations, setLocations] = useState<{ value: string; count: number }[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [detailData, setDetailData] = useState<Record<string, unknown> | null>(null);
  const [jobSkills, setJobSkills] = useState<JobSkill[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sort_by: 'scraped_at',
        sort_order: 'DESC',
      });
      if (search) params.set('search', search);
      if (expFilter) params.set('experience_level', expFilter);
      if (companyFilter) params.set('company', companyFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (skillFilter) params.set('skill', skillFilter);
      if (sectorFilter) params.set('category', sectorFilter);
      if (countryFilter) params.set('country', countryFilter);
      if (locationFilter) params.set('location', locationFilter);
      if (salaryFilter) params.set('salary', salaryFilter);

      const data = await api.get<{ data?: Job[]; pagination?: Pagination | null }>(
        `/api/jobs?${params}`,
        { retry: 2 },
      );
      setJobs(data.data || []);
      setPagination(data.pagination || null);
    } catch (e) {
      setLoadError(errorText(e, 'Failed to load jobs'));
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, expFilter, companyFilter, statusFilter, skillFilter, sectorFilter, countryFilter, locationFilter, salaryFilter]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  useEffect(() => {
    fetch(`${API_BASE}/api/companies`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setCompanies(d.companies || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/jobs/facets`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setCountries(d.countries || []); setLocations(d.locations || []); })
      .catch(() => {});
  }, []);

  const sectors = useMemo(() =>
    [...new Set(companies.map((c) => c.category).filter(Boolean))].sort(),
    [companies]
  );

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
    setSkillFilter(skillInput);
    setLocationFilter(locationInput);
  };

  const startScraping = async (companyId?: number) => {
    setScraping(true);
    try {
      const path = companyId ? '/api/scrape' : '/api/scrape/all';
      const body = companyId ? { company_id: companyId } : {};
      const data = await api.post<{
        result?: { success?: boolean; jobsFound?: number };
        successful?: number;
        error?: string;
        message?: string;
      }>(path, body);
      if (data.result?.success || (data.successful ?? 0) > 0) {
        await fetchJobs();
        toast.success(`Scraping completed! Found ${data.result?.jobsFound || data.successful || 0} new jobs.`);
      } else {
        toast.error('Scraping failed: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (e) {
      toast.error(errorText(e, 'Error starting scraping process'));
    } finally {
      setScraping(false);
    }
  };

  const openDetail = (job: Job) => {
    setSelectedJob(job);
    setDetailData(null);
    setJobSkills([]);
    setLoadingDetail(true);
    Promise.all([
      fetch(`${API_BASE}/api/jobs/${job.id}`, { credentials: 'include' }).then((r) => r.json()),
      fetch(`${API_BASE}/api/jobs/${job.id}/skills`, { credentials: 'include' }).then((r) => r.json()).catch(() => ({ skills: [] })),
    ])
      .then(([detail, skillsData]) => {
        setDetailData(detail);
        setJobSkills(skillsData.skills || []);
      })
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  };

  const exportJobs = () => {
    const csvContent = [
      ['Title', 'Company', 'Location', 'Experience Level', 'Job Type', 'Salary Range', 'Posting Date', 'Application URL'],
      ...jobs.map(job => [
        job.title,
        job.company_name,
        job.location,
        job.experience_level,
        job.job_type,
        job.salary_range || 'N/A',
        job.posting_date,
        job.application_url || 'N/A'
      ])
    ].map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jobs_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Job Management</h1>
          <p className="text-gray-600 dark:text-slate-400 mt-2">
            {pagination ? `${pagination.total_count.toLocaleString()} jobs across ${companies.length} companies` : 'Loading...'}
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => startScraping()}
            disabled={scraping}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {scraping ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            {scraping ? 'Scraping...' : 'Scrape All'}
          </button>
          <button
            onClick={exportJobs}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-navy-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800">
        <div className="flex items-center mb-3">
          <Filter className="w-4 h-4 text-gray-500 dark:text-slate-400 mr-2" />
          <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300">Filters</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search title or function..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm dark:bg-slate-800 dark:text-white"
            />
          </div>
          <div className="relative">
            <Tag className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Filter by skill..."
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm dark:bg-slate-800 dark:text-white"
            />
          </div>
          <div className="relative">
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500" />
            <input
              type="text"
              list="job-location-options"
              placeholder="Filter by location..."
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm dark:bg-slate-800 dark:text-white"
            />
            <datalist id="job-location-options">
              {locations.map((loc) => (
                <option key={loc.value} value={loc.value}>{loc.count} job{loc.count === 1 ? '' : 's'}</option>
              ))}
            </datalist>
          </div>
          <select
            value={countryFilter}
            onChange={(e) => { setCountryFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm dark:bg-slate-800 dark:text-white"
          >
            <option value="">All Countries</option>
            {countries.map((ct) => (
              <option key={ct.value} value={ct.value}>{ct.value} ({ct.count})</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm dark:bg-slate-800 dark:text-white"
          >
            <option value="open">Open Jobs</option>
            <option value="closed">Closed Jobs</option>
            <option value="all">All Jobs</option>
          </select>
          <select
            value={expFilter}
            onChange={(e) => { setExpFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm dark:bg-slate-800 dark:text-white"
          >
            <option value="">All Levels</option>
            <option value="Junior">Junior</option>
            <option value="Mid-level">Mid-level</option>
            <option value="Senior">Senior</option>
            <option value="Executive">Executive</option>
          </select>
          <select
            value={sectorFilter}
            onChange={(e) => { setSectorFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm dark:bg-slate-800 dark:text-white"
          >
            <option value="">All Sectors</option>
            {sectors.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={companyFilter}
            onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm dark:bg-slate-800 dark:text-white"
          >
            <option value="">All Companies</option>
            {companies.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
          <select
            value={salaryFilter}
            onChange={(e) => { setSalaryFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm dark:bg-slate-800 dark:text-white"
          >
            <option value="">Any Salary</option>
            <option value="disclosed">Salary disclosed</option>
          </select>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
          >
            Search
          </button>
        </div>
        {skillFilter && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-slate-400">Skill filter:</span>
            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
              {skillFilter}
              <button onClick={() => { setSkillFilter(''); setSkillInput(''); setPage(1); }} className="ml-1 hover:text-primary-600">
                <X className="w-3 h-3" />
              </button>
            </span>
          </div>
        )}
      </div>

      {/* Jobs List */}
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Jobs {pagination ? `(${pagination.total_count.toLocaleString()})` : ''}
          </h3>
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-slate-400">
              <button
                onClick={() => setPage(page - 1)}
                disabled={!pagination.has_prev_page}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span>Page {pagination.current_page} of {pagination.total_pages}</span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={!pagination.has_next_page}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-8"><SkeletonList rows={6} /></div>
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={fetchJobs} />
        ) : jobs.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-slate-400">No jobs found matching your criteria</p>
            <button
              onClick={() => startScraping()}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Start Scraping Jobs
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-slate-800">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                onClick={() => openDetail(job)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">{job.title}</h4>
                      <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${
                        job.status === 'open' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {job.status === 'open' ? 'Open' : 'Closed'}
                      </span>
                      <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${
                        job.experience_level === 'Executive' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                        job.experience_level === 'Senior' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400' :
                        job.experience_level === 'Mid-level' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                        'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300'
                      }`}>
                        {job.experience_level}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-slate-400">
                      <span className="flex items-center">
                        <Building className="w-3 h-3 mr-1" />{job.company_name}
                      </span>
                      <span className="flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />{job.location}
                      </span>
                      <span className="flex items-center">
                        <Briefcase className="w-3 h-3 mr-1" />{job.job_type}
                      </span>
                      {job.salary_range && (
                        <span className="flex items-center">
                          <DollarSign className="w-3 h-3 mr-1" />{job.salary_range}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                    {job.application_url && (
                      <a
                        href={job.application_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="px-2.5 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />Apply
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-slate-800 flex items-center justify-between text-sm text-gray-600 dark:text-slate-400">
            <span>
              Showing {(pagination.current_page - 1) * pagination.limit + 1}–{Math.min(pagination.current_page * pagination.limit, pagination.total_count)} of {pagination.total_count.toLocaleString()}
            </span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setPage(1)}
                disabled={!pagination.has_prev_page}
                className="px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"
              >
                First
              </button>
              <button
                onClick={() => setPage(page - 1)}
                disabled={!pagination.has_prev_page}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {/* Page number buttons */}
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                const startPage = Math.max(1, Math.min(page - 2, pagination.total_pages - 4));
                const p = startPage + i;
                if (p > pagination.total_pages) return null;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-2.5 py-1 rounded text-sm ${p === page ? 'bg-primary-600 text-white' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(page + 1)}
                disabled={!pagination.has_next_page}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(pagination.total_pages)}
                disabled={!pagination.has_next_page}
                className="px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      <Modal
        isOpen={!!selectedJob}
        onClose={() => setSelectedJob(null)}
        title={selectedJob?.title || 'Job details'}
        size="lg"
      >
        {selectedJob && (
          <>
            <div className="mb-4 flex items-center space-x-3 text-sm text-gray-600 dark:text-slate-400">
              <span className="flex items-center"><Building className="w-4 h-4 mr-1" />{selectedJob.company_name}</span>
              <span className="flex items-center"><MapPin className="w-4 h-4 mr-1" />{selectedJob.location}</span>
            </div>

            {loadingDetail ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 text-gray-400 dark:text-slate-500 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-slate-400">Loading details...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    selectedJob.status === 'open' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    {selectedJob.status === 'open' ? 'Open' : 'Closed'}
                  </span>
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    selectedJob.experience_level === 'Executive' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                    selectedJob.experience_level === 'Senior' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400' :
                    'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300'
                  }`}>
                    {selectedJob.experience_level}
                  </span>
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300">
                    {selectedJob.job_type}
                  </span>
                  {detailData && Boolean(detailData.remote_policy) && detailData.remote_policy !== 'unknown' && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      {String(detailData.remote_policy)}
                    </span>
                  )}
                  {selectedJob.salary_range && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                      {selectedJob.salary_range}
                    </span>
                  )}
                </div>

                {/* Skills */}
                {jobSkills.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Skills ({jobSkills.length})</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {jobSkills.map((s) => (
                        <span
                          key={s.skill}
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${SKILL_CATEGORY_COLORS[s.skill_category] || 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300'}`}
                          title={`${s.skill_category} (${s.source})`}
                        >
                          {s.skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Description</h3>
                  {(() => {
                    const raw = detailData
                      ? String(detailData.description_plain || detailData.description || '')
                      : (selectedJob.description_plain || selectedJob.description || '');
                    const clean = raw ? stripHtml(raw) : '';
                    return clean ? (
                      <div className="text-sm text-gray-600 dark:text-slate-400 whitespace-pre-line max-h-80 overflow-y-auto leading-relaxed">
                        {clean.slice(0, 5000)}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-slate-400">No description available.</p>
                    );
                  })()}
                </div>

                {/* Apply button */}
                {Boolean(selectedJob.application_url || (detailData && detailData.application_url)) && (
                  <a
                    href={String(detailData?.application_url || selectedJob.application_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Apply Now
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default Jobs;
